import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, X, FileText } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { LibraryItem } from '@/data/mock'
import type { BulkUploadResponse } from '@/types/api'
import { apiFetch } from '@/lib/apiFetch'

interface UploadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (item: LibraryItem, replaceId?: string) => void
  onSetActiveDocumentId?: (id: string) => void
  workspaceId: string | null
  onRefreshLibrary?: () => void
}

// Mirrors MAX_FILES_PER_UPLOAD in backend/main.py. The backend enforces
// the same cap and returns 413 if exceeded — this client check is for UX
// only. Raise both together if the backend gets a real async worker.
const MAX_FILES = 20
const UPLOAD_CONCURRENCY = 3

export function UploadModal({ open, onOpenChange, onUpload, onSetActiveDocumentId, workspaceId, onRefreshLibrary }: UploadModalProps) {
  const [stagedFiles, setStagedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setStagedFiles((prev) => {
      const next = [...prev, ...acceptedFiles]
      if (next.length > MAX_FILES) {
        toast.warning(`Up to ${MAX_FILES} files at a time. Extras were dropped.`)
        return next.slice(0, MAX_FILES)
      }
      return next
    })
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  })

  const removeFile = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (stagedFiles.length === 0) return
    setIsUploading(true)

    const tasks = stagedFiles.map((file, index) => {
      const tempId = `lib-upload-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`
      const ghost: LibraryItem = {
        id: tempId,
        filename: file.name,
        title: file.name.replace(/\.pdf$/i, ''),
        fileSubtype: 'Data on File',
        amaCitation: file.name.replace(/\.pdf$/i, ''),
        status: 'processing',
        workspaceId: workspaceId ?? '',
        isActiveInRag: true,
      }
      onUpload(ghost)
      return { file, ghost }
    })

    setStagedFiles([])
    onOpenChange(false)

    let processingCount = 0
    let linkedCount = 0
    let errorCount = 0
    let firstSuccessId: string | null = null
    const seenSourceIds = new Set<string>()

    const uploadOne = async ({ file, ghost }: (typeof tasks)[number]) => {
      const formData = new FormData()
      formData.append('files', file)
      if (workspaceId) formData.append('workspace_id', workspaceId)

      try {
        const res = await apiFetch('/api/sources/upload', { method: 'POST', body: formData })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error((err as { detail?: string }).detail ?? `HTTP ${res.status}`)
        }
        const data: BulkUploadResponse = await res.json()
        const result = data.results[0]
        if (!result) throw new Error('Empty response')

        if (result.source_id && seenSourceIds.has(result.source_id)) {
          linkedCount++
          return
        }
        if (result.source_id) seenSourceIds.add(result.source_id)

        switch (result.status) {
          case 'linked':
            linkedCount++
            if (!firstSuccessId && result.source_id) firstSuccessId = result.source_id
            onUpload({ ...ghost, id: result.source_id!, status: 'indexed' }, ghost.id)
            break
          case 'processing':
            processingCount++
            if (!firstSuccessId && result.source_id) firstSuccessId = result.source_id
            onUpload({ ...ghost, id: result.source_id!, status: 'processing' }, ghost.id)
            break
          case 'error':
            errorCount++
            onUpload({ ...ghost, status: 'failed' })
            toast.error(`Failed: ${result.filename}${result.error ? ` — ${result.error}` : ''}`)
            break
        }
      } catch (err) {
        errorCount++
        onUpload({ ...ghost, status: 'failed' })
        toast.error(`Upload failed: ${file.name} — ${err instanceof Error ? err.message : String(err)}`)
      }
    }

    const queue = [...tasks]
    const workerCount = Math.min(UPLOAD_CONCURRENCY, queue.length)
    const workers = Array.from({ length: workerCount }, async () => {
      while (queue.length > 0) {
        const next = queue.shift()
        if (!next) break
        await uploadOne(next)
      }
    })
    await Promise.all(workers)

    if (linkedCount > 0) {
      onRefreshLibrary?.()
    }

    const total = tasks.length
    const parts: string[] = []
    if (processingCount > 0) parts.push(`${processingCount} processing`)
    if (linkedCount > 0) parts.push(`${linkedCount} already linked`)
    if (errorCount > 0) parts.push(`${errorCount} failed`)
    const summary = `Uploaded ${total} file${total > 1 ? 's' : ''}: ${parts.join(', ')}`

    if (errorCount === total) {
      toast.error(summary)
    } else if (errorCount > 0) {
      toast.warning(summary)
    } else {
      toast.success(summary)
    }

    if (firstSuccessId) {
      onSetActiveDocumentId?.(firstSuccessId)
    }

    setIsUploading(false)
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) setStagedFiles([])
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Upload to Library</DialogTitle>
        </DialogHeader>

        {/* Drop Zone */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-pharma-primary bg-pharma-primary-light'
              : 'border-border hover:border-muted-foreground'
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-foreground font-medium mb-1">
            {isDragActive ? 'Drop files here...' : 'Drag and drop PDF files here'}
          </p>
          <p className="text-xs text-muted-foreground">
            or click to select files (up to {MAX_FILES})
          </p>
        </div>

        {/* Staged Files */}
        {stagedFiles.length > 0 && (
          <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto">
            {stagedFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 rounded-md px-3 py-2 bg-muted/50 text-sm"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate flex-1">{file.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeFile(index)
                  }}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={stagedFiles.length === 0 || isUploading}
            className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
          >
            Upload {stagedFiles.length > 0 ? `${stagedFiles.length} file${stagedFiles.length > 1 ? 's' : ''}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
